import { subMenus } from 'pages/Admin/AdminTools/Configuration/Features/constant'

export enum NexysShareFeature {
  Servicing = 'servicing',
}

export const NEXYS_SHARE_FEATURE_OPTIONS: Record<string, string> = {
  [NexysShareFeature.Servicing]: 'Servicing',
}

/** Maps Nexys share packages to top-level keys in admin features config. */
export const NEXYS_SHARE_FEATURE_CONFIG_KEYS: Record<string, string> = {
  [NexysShareFeature.Servicing]: 'servicing',
}

/** Pages that must never appear in shared servicing (UI or peer page-permissions). */
export const NEXYS_SHARE_EXCLUDED_PAGE_KEYS = new Set([
  'tasks',
  'orderValuation',
  'budget',
  'escrowLedger',
  // Not wired for shared page data yet — opening them only produces empty payloads / toasts.
  'globalNotes',
  'fci',
  'investors',
])

type FeatureSubmenu = string | { key: string; title?: string; isExternal?: boolean }

export interface NexysShareFeaturePage {
  key: string
  title: string
}

export function getShareFeaturePages(
  shareFeatureKey: string,
  featuresConfig: Record<string, any> = {},
): NexysShareFeaturePage[] {
  const configKey = NEXYS_SHARE_FEATURE_CONFIG_KEYS[shareFeatureKey]
  if (!configKey) return []

  const submenus: FeatureSubmenu[] = featuresConfig[configKey]?.submenus || []
  return submenus
    .map((submenu) => {
      const item = typeof submenu === 'string' ? { key: submenu } : submenu
      if (!item?.key || NEXYS_SHARE_EXCLUDED_PAGE_KEYS.has(item.key)) return null
      const title = item.title || subMenus[item.key as keyof typeof subMenus]?.title || item.key
      return { key: item.key, title }
    })
    .filter(Boolean) as NexysShareFeaturePage[]
}

export function getShareFeaturePageTitle(pageKey: string, featuresConfig?: Record<string, any>): string {
  if (featuresConfig) {
    for (const shareFeatureKey of Object.keys(NEXYS_SHARE_FEATURE_CONFIG_KEYS)) {
      const page = getShareFeaturePages(shareFeatureKey, featuresConfig).find((item) => item.key === pageKey)
      if (page) return page.title
    }
  }
  return subMenus[pageKey as keyof typeof subMenus]?.title || pageKey
}

export type NexysSharedDisplayConfig = Record<
  string,
  Record<
    string,
    {
      enabled?: boolean
      pagePermissions?: Record<string, boolean>
    }
  >
>

export function buildPeerPipelineType(peerId: string): string {
  return `NexysPeer:${peerId}`
}

export function parsePeerPipelineType(pipelineType: string): string | null {
  if (!pipelineType?.startsWith('NexysPeer:')) return null
  return pipelineType.replace('NexysPeer:', '')
}

export function buildSharedLoanDisplayId(peerId: string, loanNumber: number | string): string {
  return `nexys.${peerId}-${loanNumber}`
}

/** One shared-pipeline row's identity, positionally aligned with `rows`. */
export interface SharedPipelineRowMeta {
  loanNumber: number
  displayId: string
  sourceLoanNumber?: number
  shareIndex?: number
  locked: boolean
  servicingStatus?: string
  loanStatus?: string
}

/**
 * The servicer's own pipeline presentation, sent with the shared loans. Everything here was
 * configured by the company doing the servicing, so it renders as they set it up rather than as
 * this company's own pipeline happens to be configured.
 */
export interface SharedPipelineRequest {
  filters?: Record<string, any>
  skip?: number
  count?: number
  orderBy?: string
  orderDir?: number
  dateRange?: { from?: string | null; to?: string | null }
}

export interface SharedPipelineView {
  pipelineHeaders: string[][]
  headers: string[]
  properties: Record<string, any>
  rows: any[][]
  rowMeta: SharedPipelineRowMeta[]
  /** Loans matching the current filters. `sharedTotal` is how many are shared at all. */
  total: number
  sharedTotal: number
  totalLoanAmount: number
  skip: number
  count: number
  bubbles: Array<{ status: Record<string, boolean>; color: string; order?: string }>
  statistics: {
    byStatus: Array<{ servicingStatus: string; count: string; sum: string }>
    totalCount: number
    totalCalculationCount: number
  }
  servicingStatusLabels: Record<string, string>
  /** The servicer's filter vocabulary: filtering by this company's own would match nothing there. */
  filterOptions: {
    activeServicingStatuses: string[]
    tags: string[]
  }
}
